<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Replace the original header with PageHeader component -->
    <PageHeader title="Checklists">
      <!-- Actions slot -->
      <template #actions>
        <button 
          v-if="showCreateButton"
          @click="createNewChecklist" 
          class="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl backdrop-blur-sm transition duration-200 shadow-lg group"
        >
          <span class="material-icons text-xl group-hover:rotate-180 transition-transform duration-300">add</span>
          Nueva Checklist
        </button>
      </template>

      <!-- Filters slot -->
      <template #filters>
        <div class="relative">
          <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <span class="material-icons text-gray-400 z-10 ">search</span>
          </div>
          <input 
            v-model="searchQuery" 
            type="text" 
            placeholder="Buscar checklists..." 
            class="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl shadow-lg placeholder-white/60 text-white focus:ring-2 focus:ring-white/50 focus:border-transparent outline-none transition duration-200"
          />
        </div>
      </template>
    </PageHeader>

    <!-- Main Content -->
    <div class="p-4 sm:p-6 max-w-4xl mx-auto">
      <draggable
        v-model="sortableChecklists"
        class="grid grid-cols-1 gap-3 sm:gap-4"
        :disabled="!canReorder"
        item-key="id"
        @end="handleDragEnd"
        handle=".drag-handle"
      >
        <template #item="{ element: checklist }">
          <div class="group bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
            <div class="px-3 py-3 sm:px-4 sm:py-5 flex items-center gap-2 sm:gap-4">
              <!-- Always visible drag handle -->
              <div 
                v-if="canReorder"
                class="drag-handle cursor-move flex-shrink-0 p-1.5 sm:p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <svg class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16"></path>
                </svg>
              </div>

              <!-- Updated Title Layout -->
              <div class="flex-1 min-w-0">
                <div class="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <span v-if="checklist.number" 
                        class="inline-flex px-2 py-0.5 sm:px-3 sm:py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full w-fit"
                  >
                    #{{ checklist.number }}
                  </span>
                  <h2 class="text-base sm:text-lg font-semibold text-gray-800 break-words">
                    {{ checklist.title }}
                  </h2>
                </div>
              </div>

              <!-- Actions -->
              <div class="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                <router-link
                  :to="{ name: 'ChecklistSetup', params: { id: checklist.id } }"
                  class="inline-flex items-center gap-1 sm:gap-2 bg-green-500/90 text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg hover:bg-green-600 transition duration-200 shadow-sm complete-btn-group text-sm sm:text-base"
                >
                  <span class="material-icons text-lg sm:text-xl complete-btn-group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  <span class="font-medium hidden sm:inline">Completar</span>
                </router-link>

                <!-- Admin Actions Menu with fixed z-index -->
                <div v-if="auth.canEdit && auth.userRole.value !== 'viewer'" class="relative">
                  <button 
                    @click.stop="toggleMenu(checklist.id)"
                    class="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <span class="material-icons text-lg sm:text-xl text-gray-500">more_vert</span>
                  </button>

                  <!-- Updated dropdown menu with higher z-index -->
                  <div 
                    v-if="activeMenu === checklist.id"
                    class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl z-50 py-1 border border-gray-100 overflow-hidden"
                    @click.stop
                  >
                    <router-link 
                      :to="{ name: 'Editor', params: { id: checklist.id } }"
                      class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Editar
                    </router-link>
                    <button 
                      @click="handleDuplicate(checklist)"
                      class="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                    >
                      Duplicar
                    </button>
                    <button 
                      @click="deleteChecklist(checklist.id)"
                      class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </draggable>

      <!-- Styled Empty States -->
      <div v-if="filteredChecklists.length === 0" 
           class="bg-white rounded-xl shadow-md p-8 text-center">
        <span class="material-icons text-4xl text-gray-400 mb-3">search_off</span>
        <p class="text-gray-600">No se encontraron checklists</p>
      </div>

      <div v-if="store.checklists.length === 0" 
           class="bg-white rounded-xl shadow-md p-8 text-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p class="text-gray-600">Cargando checklists...</p>
      </div>

      <DefaultOptionsSelector
        v-if="showOptionsModal"
        @select="handleOptionsSelected"
        @cancel="cancelOptionsSelection"
      />

      <!-- Delete Confirmation Modal -->
      <ConfirmModal
        v-if="deleteConfirmation.show"
        title="Eliminar Checklist"
        message="¿Estás seguro que deseas eliminar esta checklist? Esta acción no se puede deshacer."
        @confirm="confirmDelete"
        @cancel="cancelDelete"
      />

      <!-- Add DuplicateModal -->
      <DuplicateModal
        v-if="showDuplicateModal"
        :checklist-title="selectedChecklist?.title || ''"
        @confirm="confirmDuplicate"
        @cancel="cancelDuplicate"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from '../store/store';
import { useRouter } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';
import draggable from 'vuedraggable';
import DefaultOptionsSelector from './DefaultOptionsSelector.vue';
import { useAuth } from '../firebase/auth';
import ConfirmModal from './ConfirmModal.vue';
import DuplicateModal from './DuplicateModal.vue';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import PageHeader from './PageHeader.vue'; // Import the new component

export default {
  components: {
    draggable,
    DefaultOptionsSelector,
    ConfirmModal,
    DuplicateModal,
    PageHeader // Add the new component
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const auth = useAuth();
    const searchQuery = ref('');
    const showOptionsModal = ref(false);
    const activeMenu = ref(null);

    const filteredChecklists = computed(() => {
      if (!store.checklists) return [];
      return store.checklists.filter(checklist => {
        const searchLower = searchQuery.value.toLowerCase();
        return (
          checklist?.title?.toLowerCase().includes(searchLower) ||
          checklist?.number?.toString().toLowerCase().includes(searchLower)
        );
      });
    });

    // Wait to load checklists until auth is ready
    onMounted(async () => {
      if (!auth.loading.value) {
        await store.loadChecklists();
      } else {
        watch(() => auth.loading.value, async (newValue) => {
          if (!newValue) {
            await store.loadChecklists();
          }
        });
      }
    });

    // Modified create button visibility computed property
    const showCreateButton = computed(() => {
      return !auth.loading.value && (auth.canEdit.value || auth.isAdmin.value);
    });

    const createNewChecklist = () => {
      // Show options modal first
      showOptionsModal.value = true;
    };

    const deleteConfirmation = ref({
      show: false,
      checklistId: null
    });

    const deleteChecklist = async (id) => {
      deleteConfirmation.value = {
        show: true,
        checklistId: id
      };
      activeMenu.value = null; // Close the dropdown
    };

    const confirmDelete = async () => {
      if (auth.userRole.value !== 'viewer') {
        await store.removeChecklist(deleteConfirmation.value.checklistId);
        deleteConfirmation.value = {
          show: false,
          checklistId: null
        };
      }
    };

    const cancelDelete = () => {
      deleteConfirmation.value = {
        show: false,
        checklistId: null
      };
    };

    // Restore proper handleOptionsSelected function
    const handleOptionsSelected = (options) => {
      showOptionsModal.value = false;
      
      const newId = uuidv4();
      const initialData = {
        id: newId,
        title: '',
        number: '',
        rev: 1,
        items: [],
        defaultOptions: options
      };

      // Store the data in localStorage before navigation
      localStorage.setItem('newChecklistData', JSON.stringify({
        isNew: true,
        initialData
      }));

      router.push({ 
        name: 'Editor', 
        params: { id: newId }
      });
    };

    // Add cancel handler for options modal
    const cancelOptionsSelection = () => {
      showOptionsModal.value = false;
    };

    const toggleMenu = (id) => {
      activeMenu.value = activeMenu.value === id ? null : id;
    };

    // Click outside to close menu
    onMounted(() => {
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.relative')) {
          activeMenu.value = null;
        }
      });
    });

    // Add duplication state and handlers
    const showDuplicateModal = ref(false);
    const selectedChecklist = ref(null);

    const handleDuplicate = (checklist) => {
      selectedChecklist.value = checklist;
      showDuplicateModal.value = true;
      activeMenu.value = null; // Close the dropdown
    };

    const confirmDuplicate = async () => {
      try {
        if (!selectedChecklist.value) return;
        
        await store.duplicateChecklist(selectedChecklist.value.id);
        showDuplicateModal.value = false;
        selectedChecklist.value = null;
      } catch (error) {
        console.error('Error duplicating checklist:', error);
      }
    };

    const cancelDuplicate = () => {
      showDuplicateModal.value = false;
      selectedChecklist.value = null;
    };

    // Add computed property for draggable checklists
    const sortableChecklists = computed({
      get: () => filteredChecklists.value,
      set: async (newList) => {
        if (canReorder.value) {
          // Update store's checklists directly
          store.checklists = newList;
          
          // Save the new order to Firestore
          try {
            const docRef = doc(db, 'settings', 'checklistOrder');
            await setDoc(docRef, {
              order: newList.map(cl => cl.id),
              updatedAt: new Date().toISOString()
            });
          } catch (error) {
            console.error('Failed to save checklist order:', error);
          }
        }
      }
    });

    // Computed property to determine if user can reorder
    const canReorder = computed(() => {
      return auth.isAdmin.value || auth.userRole.value === 'editor';
    });

    // Handler for drag end event
    const handleDragEnd = () => {
      // The sortableChecklists setter will handle the update
    };

    return {
      store,
      searchQuery,
      filteredChecklists,
      createNewChecklist,
      deleteChecklist,
      showOptionsModal,
      handleOptionsSelected,
      cancelOptionsSelection,
      auth,
      showCreateButton,
      activeMenu,
      toggleMenu,
      deleteConfirmation,
      confirmDelete,
      cancelDelete,
      showDuplicateModal,
      selectedChecklist,
      handleDuplicate,
      confirmDuplicate,
      cancelDuplicate,
      sortableChecklists,
      canReorder,
      handleDragEnd,
    };
  },
};
</script>

<style scoped>
.drag-handle {
  touch-action: none;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.group:hover {
  transform: translateY(-1px);
}

/* Add smooth transitions for all interactive elements */
button, a {
  transition: all 0.2s ease;
}

/* Enhance the material icons */
.material-icons {
  font-optical-sizing: auto;
  font-variation-settings: 'FILL' 1;
}

/* Add smooth box shadow transitions */
.group {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Remove truncate-related hover transform */
.group:hover {
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.05);
  transform: none;
}

/* Ensure proper text wrapping */
.break-words {
  word-break: break-word;
  hyphens: auto;
}

/* Replace group hover with scoped class for the complete button */
.complete-btn-group:hover .complete-btn-group-hover\:translate-x-1 {
  --tw-translate-x: 0.25rem;
  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}

/* Isolate transitions and transformations */
.material-icons {
  font-optical-sizing: auto;
  font-variation-settings: 'FILL' 1;
  transform: translateZ(0);
  will-change: transform;
}
</style>